// Shown on a pipeline record that several BDMs share (Incoming Leads → assign
// to multiple BDMs). Answers "is someone else already working on this?": who is
// on it, and who last did something (edit, stage move, follow-up, quotation)
// and how long ago — so nobody makes the same call twice.

import { Users } from 'lucide-react';

export interface RecordSharing {
  owner: { id: string; name: string } | null;
  coAssignees: { id: string; name: string }[];
  lastWorkedBy: { id: string; name: string } | null;
  lastWorkedAt: string | null;
}

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days} day${days === 1 ? '' : 's'} ago` : new Date(iso).toLocaleDateString('en-IN');
}

export function SharedRecordBanner({ sharing, currentUserId }: { sharing: RecordSharing | null | undefined; currentUserId?: string }) {
  if (!sharing || !sharing.coAssignees?.length) return null;
  const you = (id?: string) => (id && id === currentUserId ? ' (you)' : '');
  const people = [
    ...(sharing.owner ? [`${sharing.owner.name}${you(sharing.owner.id)} — owner`] : []),
    ...sharing.coAssignees.map((u) => `${u.name}${you(u.id)}`),
  ];
  const last = sharing.lastWorkedBy && sharing.lastWorkedAt;
  const someoneElse = last && sharing.lastWorkedBy!.id !== currentUserId;
  const recent = last && Date.now() - new Date(sharing.lastWorkedAt!).getTime() < 24 * 3600 * 1000;

  return (
    <div className={`flex flex-wrap items-start gap-3 rounded-2xl border p-4 ${someoneElse && recent ? 'border-amber-300 bg-amber-50' : 'border-indigo-200 bg-indigo-50/70'}`}>
      <Users className={`mt-0.5 h-4 w-4 shrink-0 ${someoneElse && recent ? 'text-amber-600' : 'text-indigo-600'}`} />
      <div className="text-sm">
        <p className="font-bold text-slate-900">Shared lead — {people.length} BDMs are on this record</p>
        <p className="text-slate-700">{people.join(' · ')}</p>
        <p className={`mt-1 font-semibold ${someoneElse && recent ? 'text-amber-800' : 'text-slate-600'}`}>
          {last
            ? <>Last worked on by {sharing.lastWorkedBy!.name}{you(sharing.lastWorkedBy!.id)}, {timeAgo(sharing.lastWorkedAt!)}
                {someoneElse && recent ? ' — check with them before contacting the client.' : '.'}</>
            : 'Nobody has worked on it yet.'}
        </p>
      </div>
    </div>
  );
}
